import {
    CustomListItemId,
    PlaybookTouchpointId,
    RegionId,
    TouchpointKind,
} from "@sp-crm/core";
import type { AppRouter } from "@sp-crm/server/rpc";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { inferProcedureOutput } from "@trpc/server";
import { trpc } from "components/rpc/trpc";
import { DeleteButton } from "components/ui/action-button";
import { AutosavingInput } from "components/ui/autosaving-input";
import { PrimaryButton } from "components/ui/primary-button";
import React from "react";
import { useFeature, useRegionId } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { TierColorPicker } from "./tier-color-picker";
import { TierTouchpointsEditor } from "./tier-touchpoints-editor";
import {
    asCadenceUnitInput,
    asTouchpointKinds,
    CadenceUnitInput,
    groupTouchpointsByTier,
} from "./touchpoint-cadence";

type Tier = inferProcedureOutput<AppRouter["referralTier"]["tiers"]>[number];
type Touchpoint = inferProcedureOutput<
    AppRouter["marketingPlaybook"]["tierTouchpoints"]
>[number];

export const ReferralTierEditor: React.FC<unknown> = () => {
    const trpcClient = trpc.useTRPC();
    const tiersQuery = useQuery(trpcClient.referralTier.tiers.queryOptions());
    const marketingPlaybookEnabled = useFeature("marketingPlaybook");
    const visitsEnabled = useFeature("visits");
    const regionId = useRegionId();

    const touchpointsQuery = useQuery({
        ...trpcClient.marketingPlaybook.tierTouchpoints.queryOptions({
            regionId: regionId ?? ("" as RegionId),
        }),
        enabled: marketingPlaybookEnabled && !!regionId,
    });

    const onSuccess = () => {
        tiersQuery.refetch();
    };

    const onTouchpointSuccess = () => {
        touchpointsQuery.refetch();
    };

    const createTier = useMutation({
        ...trpcClient.referralTier.createTier.mutationOptions(),
        onSuccess,
    });
    const patchTier = useMutation({
        ...trpcClient.referralTier.patchTier.mutationOptions(),
        onSuccess,
    });
    const createTouchpoint = useMutation({
        ...trpcClient.marketingPlaybook.createTouchpoint.mutationOptions(),
        onSuccess: onTouchpointSuccess,
    });
    const updateTouchpoint = useMutation({
        ...trpcClient.marketingPlaybook.updateTouchpoint.mutationOptions(),
        onSuccess: onTouchpointSuccess,
    });
    const deleteTouchpoint = useMutation({
        ...trpcClient.marketingPlaybook.deleteTouchpoint.mutationOptions(),
        onSuccess: onTouchpointSuccess,
    });

    const tiers: Tier[] = (tiersQuery.data ?? []).filter(t => !t.isArchived);
    const touchpointsByTier = groupTouchpointsByTier(touchpointsQuery.data ?? []);
    const touchpointById = (id: string): Touchpoint | undefined =>
        (touchpointsQuery.data ?? []).find(t => t.id === id);

    const renameTier = (itemId: CustomListItemId, name: string) => {
        handleEvent("referral-tier-update", { field: "name" });
        patchTier.mutate({ itemId, name, color: null, order: null, isArchived: null });
    };

    const recolorTier = (itemId: CustomListItemId, color: string) => {
        handleEvent("referral-tier-update", { field: "color" });
        patchTier.mutate({ itemId, name: null, color, order: null, isArchived: null });
    };

    const archiveTier = (itemId: CustomListItemId) => {
        handleEvent("referral-tier-archive");
        patchTier.mutate({
            itemId,
            name: null,
            color: null,
            order: null,
            isArchived: true,
        });
    };

    const addTouchpoint = (tierListItemId: CustomListItemId) => {
        if (!regionId) {
            return;
        }
        handleEvent("marketing-touchpoint-create");
        createTouchpoint.mutate({
            regionId,
            tierListItemId,
            kinds: ["email"],
            intervalUnit: "Weekly",
            intervalCount: 1,
        });
    };

    const updateTouchpointCount = (id: string, intervalCount: number) => {
        const existing = touchpointById(id);
        if (!regionId || !existing) {
            return;
        }
        handleEvent("marketing-touchpoint-update", { field: "count" });
        updateTouchpoint.mutate({
            regionId,
            id: id as PlaybookTouchpointId,
            kinds: asTouchpointKinds(existing.kinds),
            intervalUnit: asCadenceUnitInput(existing.intervalUnit),
            intervalCount,
        });
    };

    const updateTouchpointUnit = (id: string, intervalUnit: CadenceUnitInput) => {
        const existing = touchpointById(id);
        if (!regionId || !existing) {
            return;
        }
        handleEvent("marketing-touchpoint-update", { field: "unit" });
        updateTouchpoint.mutate({
            regionId,
            id: id as PlaybookTouchpointId,
            kinds: asTouchpointKinds(existing.kinds),
            intervalUnit,
            intervalCount: existing.intervalCount,
        });
    };

    const updateTouchpointKinds = (id: string, kinds: TouchpointKind[]) => {
        const existing = touchpointById(id);
        if (!regionId || !existing) {
            return;
        }
        handleEvent("marketing-touchpoint-update", { field: "kinds" });
        updateTouchpoint.mutate({
            regionId,
            id: id as PlaybookTouchpointId,
            kinds,
            intervalUnit: asCadenceUnitInput(existing.intervalUnit),
            intervalCount: existing.intervalCount,
        });
    };

    const deleteTouchpointById = (id: string) => {
        if (!regionId) {
            return;
        }
        handleEvent("marketing-touchpoint-delete");
        deleteTouchpoint.mutate({ regionId, id: id as PlaybookTouchpointId });
    };

    return (
        <div className="space-y-4 max-w-2xl">
            {tiers.map(tier => {
                const itemId = tier.id as CustomListItemId;
                return (
                    <div
                        key={tier.id}
                        className="space-y-3 rounded-lg border border-gray-200 p-4">
                        <div className="flex flex-wrap items-center gap-3">
                            <div className="flex-1 min-w-[12rem]">
                                <AutosavingInput
                                    initial={tier.name}
                                    onCommit={newVal => renameTier(itemId, newVal)}
                                />
                            </div>
                            <TierColorPicker
                                name={tier.name || "Tier"}
                                color={tier.color}
                                onSelect={token => recolorTier(itemId, token)}
                            />
                            <DeleteButton
                                backgroundColor="bg-white"
                                onClick={() => archiveTier(itemId)}
                                confirm={{
                                    title: "Remove tier?",
                                    message: `Are you sure you want to remove the "${
                                        tier.name || "Tier"
                                    }" tier?`,
                                    confirmLabel: "Yes, remove",
                                }}
                            />
                        </div>
                        {marketingPlaybookEnabled && regionId ? (
                            <div className="border-t border-gray-100 pt-3">
                                <TierTouchpointsEditor
                                    touchpoints={touchpointsByTier.get(tier.id) ?? []}
                                    visitsEnabled={visitsEnabled}
                                    onAdd={() => addTouchpoint(itemId)}
                                    onUpdateCount={updateTouchpointCount}
                                    onUpdateUnit={updateTouchpointUnit}
                                    onUpdateKinds={updateTouchpointKinds}
                                    onDelete={deleteTouchpointById}
                                />
                            </div>
                        ) : null}
                    </div>
                );
            })}
            <PrimaryButton
                onClick={() => {
                    handleEvent("referral-tier-create");
                    createTier.mutate({ name: "New tier", color: "gray" });
                }}>
                Add tier
            </PrimaryButton>
        </div>
    );
};
